import { useState } from 'react';
import { site } from '../data/site';
import { Icon, Ornament, Reveal, SectionHeading } from '../components/Primitives';
import Seo from '../components/Seo';
import { PageHero } from '../components/Shared';

const OFFER = [
  {
    Glyph: Icon.Diamond,
    title: 'Bridal suites, ready to move',
    text: 'Complete polki and kundan sets — collar, haar, chandbali, tikka, nath, kada — held in depth through the wedding season.',
  },
  {
    Glyph: Icon.Ring,
    title: 'Made against your designs',
    text: 'Send a sketch, a reference photo, or a piece to match. We quote on weight and setting, then produce to your specification.',
  },
  {
    Glyph: Icon.Crown,
    title: 'Loose polki and colour',
    text: 'Uncut diamond, emerald, ruby and pearl supplied loose for jewellers running their own workshops.',
  },
  {
    Glyph: Icon.Handshake,
    title: 'Exhibition supply',
    text: 'We show at trade exhibitions across Gujarat. Ask for the current schedule and we will hold a counter for you.',
  },
];

const STEPS = [
  ['Get in touch', 'Send your firm name, city and what you are looking for. WhatsApp is fastest.'],
  ['We share a selection', 'Photographs and trade pricing against your category and budget.'],
  ['Confirm and produce', 'Stock ships from Ahmedabad; made-to-order work is scheduled with a delivery date.'],
];

export default function Wholesale() {
  const [form, setForm] = useState({ firm: '', name: '', city: '', interest: '', note: '' });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  /* Static hosting means no server to post to — compose the enquiry into the
     channels the shop already reads instead of silently dropping it. */
  const message = [
    'Wholesale enquiry — Nikhil Jewellers',
    form.firm && `Firm: ${form.firm}`,
    form.name && `Contact: ${form.name}`,
    form.city && `City: ${form.city}`,
    form.interest && `Interested in: ${form.interest}`,
    form.note && `Notes: ${form.note}`,
  ]
    .filter(Boolean)
    .join('\n');

  const waHref = `${site.whatsappHref}?text=${encodeURIComponent(message)}`;
  const mailHref = `${site.emailHref}?subject=${encodeURIComponent(
    'Wholesale enquiry'
  )}&body=${encodeURIComponent(message)}`;

  return (
    <>
      <Seo
        title="Wholesale"
        description="Trade supply from Nikhil Jewellers, Ahmedabad — bridal polki and kundan suites, loose stones and made-to-order work for jewellers across Gujarat."
      />

      <PageHero
        eyebrow="For the Trade"
        title="Wholesale supply to jewellers"
        lede="Roughly half of what leaves this building goes to other jewellers. If you retail bridal jewellery, we can supply it — in stock, or made to your design."
        image="/assets/products/arch-bridal-collar.webp"
        trail={[{ label: 'Home', to: '/' }, { label: 'Wholesale' }]}
      />

      {/* What we supply */}
      <section className="bg-cream-50 py-16 sm:py-24">
        <div className="shell">
          <SectionHeading
            eyebrow="What we supply"
            title="Four ways we work with retailers"
          />

          <div className="mt-14 grid gap-px bg-royal-800/10 sm:grid-cols-2">
            {OFFER.map(({ Glyph, title, text }, i) => (
              <Reveal key={title} delay={i * 90} className="bg-cream-50 p-9">
                <Glyph className="h-7 w-7 text-gold-600" />
                <h3 className="mt-5 font-display text-2xl font-normal text-royal-900">{title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-royal-700/70">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="noise-navy relative overflow-hidden py-20 sm:py-24">
        <div className="shell relative">
          <SectionHeading tone="light" eyebrow="How it works" title="Three steps" />

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {STEPS.map(([title, text], i) => (
              <Reveal key={title} delay={i * 100}>
                <div className="border-t border-gold-500/30 pt-6">
                  <p className="font-display text-5xl font-light text-gold-400/45">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-2xl font-normal text-cream-50">{title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-cream-200/70">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section className="bg-cream-100 py-16 sm:py-24">
        <div className="shell">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <p className="eyebrow">Trade Enquiry</p>
              <h2 className="display-lg mt-4 text-royal-900">Tell us about your shop</h2>
              <Ornament className="mt-6 !justify-start" />
              <p className="mt-7 lede">
                Fill this in and it opens a pre-written message in WhatsApp — the
                quickest way to reach the counter. Prefer email? That button composes
                the same details instead.
              </p>

              <div className="mt-9 space-y-4 border-t border-royal-800/10 pt-7">
                <a
                  href={site.phoneHref}
                  className="link-underline flex items-center gap-3 text-[15px] text-royal-800"
                >
                  <Icon.Phone className="h-4 w-4 text-gold-600" />
                  {site.phone}
                </a>
                <a
                  href={site.emailHref}
                  className="link-underline flex items-center gap-3 break-all text-[15px] text-royal-800"
                >
                  <Icon.Mail className="h-4 w-4 shrink-0 text-gold-600" />
                  {site.email}
                </a>
                <p className="flex items-start gap-3 text-[15px] leading-relaxed text-royal-800/80">
                  <Icon.Pin className="mt-1 h-4 w-4 shrink-0 text-gold-600" />
                  {site.addressOneLine}
                </p>
              </div>
            </Reveal>

            <Reveal delay={120} className="lg:col-span-7">
              <form
                className="space-y-5 border border-royal-800/10 bg-cream-50 p-7 sm:p-10"
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Firm name" value={form.firm} onChange={set('firm')} required />
                  <Field label="Your name" value={form.name} onChange={set('name')} required />
                  <Field label="City" value={form.city} onChange={set('city')} />
                  <div>
                    <label
                      htmlFor="interest"
                      className="block text-[11px] font-medium uppercase tracking-wide2 text-royal-800/70"
                    >
                      Interested in
                    </label>
                    <select
                      id="interest"
                      value={form.interest}
                      onChange={set('interest')}
                      className="mt-2 w-full border border-royal-800/15 bg-cream-50 px-4 py-3
                        text-[15px] text-royal-900 outline-none transition-colors
                        focus:border-gold-500"
                    >
                      <option value="">Select…</option>
                      <option>Bridal polki suites</option>
                      <option>Kundan sets</option>
                      <option>Necklaces &amp; collars</option>
                      <option>Earrings</option>
                      <option>Bangles &amp; bracelets</option>
                      <option>Rings</option>
                      <option>Loose polki &amp; coloured stones</option>
                      <option>Made-to-order work</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="note"
                    className="block text-[11px] font-medium uppercase tracking-wide2 text-royal-800/70"
                  >
                    Anything else
                  </label>
                  <textarea
                    id="note"
                    rows={4}
                    value={form.note}
                    onChange={set('note')}
                    placeholder="Quantities, budget range, delivery timeline…"
                    className="mt-2 w-full resize-y border border-royal-800/15 bg-cream-50 px-4 py-3
                      text-[15px] text-royal-900 outline-none transition-colors
                      placeholder:text-royal-700/35 focus:border-gold-500"
                  />
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="btn-primary flex-1 sm:flex-none"
                  >
                    <Icon.WhatsApp className="h-4 w-4" />
                    Send on WhatsApp
                  </a>
                  <a href={mailHref} className="btn-outline flex-1 sm:flex-none">
                    <Icon.Mail className="h-4 w-4" />
                    Send by email
                  </a>
                </div>

                <p className="pt-1 text-[12px] leading-relaxed text-royal-700/55">
                  Trade pricing is shared with registered jewellers. We may ask for
                  your GST number before quoting.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, value, onChange, required }) {
  const id = label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[11px] font-medium uppercase tracking-wide2 text-royal-800/70"
      >
        {label}
        {required && <span className="text-maroon-700"> *</span>}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        className="mt-2 w-full border border-royal-800/15 bg-cream-50 px-4 py-3 text-[15px]
          text-royal-900 outline-none transition-colors focus:border-gold-500"
      />
    </div>
  );
}
